"use client";
import { Suspense } from "react";

import Link from "next/link";
import { useSearchParams } from "next/navigation";

import { Button } from "@heroui/button";
import { Card, CardBody, CardHeader } from "@heroui/card";
import { CheckCircle, Clock, Mail, Users } from "lucide-react";

import { PageViewTracker } from "@/components/observability/PageViewTracker";
import { EventKeys } from "@/lib/observability/catalog";
import { routes } from "@/lib/routes";

function RegistrationSubmittedContent() {
  const searchParams = useSearchParams();
  const email = searchParams.get("email") ?? "your email address";
  const organizationName =
    searchParams.get("organization") ?? "the organization";

  return (
    <div className="bg-default-50">
      <div className="w-full max-w-2xl mx-auto py-16 px-4 sm:px-0">
        <Card className="max-w-2xl w-full shadow-sm border-default-200">
          <CardHeader className="pb-6 flex- flex-wrap justify-start">
            <div className="mr-4 mb-4 p-3 bg-success-100 rounded-full w-fit">
              <CheckCircle className="h-8 w-8 text-success-600" />
            </div>
            <h1 className="text-2xl font-bold text-default-900 mb-2">
              Application Submitted Successfully!
            </h1>
            <p className="text-default-600 w-full">
              Thank you for applying to become a case worker with{" "}
              {organizationName}.
            </p>
          </CardHeader>

          <CardBody className="space-y-6">
            {/* What happens next section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                What Happens Next
              </h2>
              <div className="space-y-3 text-sm text-blue-800">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-blue-800 text-xs font-bold mt-0.5">
                    1
                  </div>
                  <div>
                    <strong>Email Verification:</strong> Check your email at
                    <span className="font-mono bg-blue-100 px-1 rounded">
                      {" "}
                      {email}{" "}
                    </span>
                    and click the verification link to activate your account.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-blue-800 text-xs font-bold mt-0.5">
                    2
                  </div>
                  <div>
                    <strong>Application Review:</strong> Local coordinators and
                    administrators will review your application and background.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-blue-800 text-xs font-bold mt-0.5">
                    3
                  </div>
                  <div>
                    <strong>Decision Notification:</strong> You'll receive an
                    email with the decision within 2-3 business days.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-blue-800 text-xs font-bold mt-0.5">
                    4
                  </div>
                  <div>
                    <strong>Access Granted:</strong> If approved, you'll gain
                    access to the case worker dashboard to start helping
                    families.
                  </div>
                </div>
              </div>
            </div>

            {/* Important reminders */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-md font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Important Reminders
              </h3>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>
                  • Check your spam/junk folder if you don't see the
                  verification email
                </li>
                <li>
                  • Keep your email address updated in case we need to contact
                  you
                </li>
                <li>
                  • You can log in once your email is verified, but dashboard
                  access requires approval
                </li>
              </ul>
            </div>

            {/* What case workers do */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-md font-semibold text-green-900 mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                What You'll Be Able to Do
              </h3>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Post needs for families requiring assistance</li>
                <li>• Coordinate with volunteers who offer help</li>
                <li>• Track progress and mark needs as completed</li>
                <li>
                  • Serve as a bridge between families and community support
                </li>
              </ul>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Link href={routes.auth.login()}>
                <Button className="flex-1" color="primary">
                  Go to Login
                </Button>
              </Link>
              <Link href={routes.auth.resendVerification()}>
                <Button className="flex-1" color="default" variant="flat">
                  Resend Verification Email
                </Button>
              </Link>
            </div>

            {/* Contact support */}
            <div className="text-center text-sm text-default-500 pt-4 border-t border-default-200">
              <p>
                Questions about your application?{" "}
                <Link
                  className="text-primary hover:underline"
                  href={routes.public.contact()}
                >
                  Contact Support
                </Link>
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default function RegistrationSubmittedPage() {
  return (
    <>
      <PageViewTracker
        eventKey={EventKeys.PUBLIC_REGISTRATION_SUBMITTED_PAGE_VIEW}
      />

      <Suspense
        fallback={
          <div className="bg-default-50">
            <div className="w-full max-w-2xl mx-auto py-16 px-4 sm:px-0">
              <div className="p-6 bg-white rounded-lg shadow-sm border border-default-200 max-w-md w-full mx-auto">
                <div className="text-center">Loading...</div>
              </div>
            </div>
          </div>
        }
      >
        <RegistrationSubmittedContent />
      </Suspense>
    </>
  );
}
